import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import { Checkbox } from "@mui/material";

export default function C_E_MULTI({
  editorState,
  onSave,
  closeEditor,

  value,
  tempValue,
  onChange,
  onBlur,
  onKeyDown,
  field,
  inPopup = false,
}) {
  const properties_state = useSelector((state) => state.properties);

  // 1. Determine active data based on mode
  const activeField = inPopup ? field : editorState?.field;
  const activeValue = inPopup ? tempValue : editorState?.value;
  const isAllowOtherAnswer = inPopup
    ? activeField?.isAllowOtherAnswer
    : editorState?.isAllowOtherAnswer;

  const baseOptions = inPopup
    ? activeField?.array_templates || []
    : editorState?.options?.length > 0
      ? editorState.options
      : activeField?.array_templates || [];

  const [filter_text, setFilterText] = useState("");
  const [raw_filter_text, setRawFilterText] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // For popup mode

  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const popupAnchorRef = useRef(null); // Reference for the popup mode anchor

  const currentAnchorEl = inPopup
    ? popupAnchorRef.current
    : editorState?.anchorEl;
  const isMenuOpen = inPopup ? isDropdownOpen : true;

  // Parse initial value into a clean array
  const currentValues =
    typeof activeValue === "string" && activeValue
      ? activeValue.split("\n").filter(Boolean)
      : Array.isArray(activeValue)
        ? activeValue
        : [];

  // 2. Only auto-focus the search bar when the menu actually opens
  useEffect(() => {
    if (isMenuOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isMenuOpen]);

  const filterOnChange = (e) => {
    const rawText = e?.target?.value || "";
    setRawFilterText(rawText);
    setFilterText(rawText.toLowerCase());
  };

  const handleClose = () => {
    if (inPopup) {
      setIsDropdownOpen(false);
      if (onBlur) onBlur(); // Trigger save/blur logic on close
    } else {
      if (closeEditor) closeEditor();
    }
  };

  // --- TOGGLE LOGIC: Saves immediately, leaves popover open ---
  const handleToggle = (e, optValue) => {
    if (e) e.stopPropagation();

    let newValues;
    if (currentValues.includes(optValue)) {
      newValues = currentValues.filter((v) => v !== optValue);
    } else {
      newValues = [...currentValues, optValue];
    }

    const savedString = newValues.join("\n");

    if (inPopup) {
      if (onChange) onChange({ target: { value: savedString } });
      // Do NOT close dropdown so they can select multiple items
    } else {
      onSave(editorState.row.id, activeField.key, savedString, activeValue);
    }

    // Clear search term if they added a custom answer
    if (
      isAllowOtherAnswer &&
      !baseOptions.some((o) => (o?.name || o) === optValue)
    ) {
      setRawFilterText("");
      setFilterText("");
      searchRef.current?.focus();
    }
  };

  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    if (inPopup) {
      if (onChange) onChange({ target: { value: "" } });
    } else {
      onSave(editorState.row.id, activeField.key, "", activeValue);
    }
  };

  // --- DATA PREPARATION ---
  const filtered_base_options = baseOptions.filter((opt) => {
    const optLabel = typeof opt === "string" ? opt : opt?.name || "";
    if (!filter_text) return true;

    const keywords = filter_text;
    const regex_string = `${keywords
      ?.split(" ")
      ?.map((word) => `(?=.*?${word})`)
      ?.join("")}.*`;
    const regex_pattern = new RegExp(regex_string);
    return regex_pattern.test(String(optLabel).toLowerCase());
  });

  let missing_selected_values = currentValues.filter(
    (val) => val && !baseOptions.some((opt) => (opt?.name || opt) === val),
  );

  if (filter_text) {
    missing_selected_values = missing_selected_values.filter((val) =>
      String(val).toLowerCase().includes(filter_text),
    );
  }

  const hasExactMatch =
    filtered_base_options.some((opt) => {
      const optLabel = typeof opt === "string" ? opt : opt?.name || "";
      return String(optLabel).toLowerCase() === filter_text;
    }) ||
    missing_selected_values.some(
      (val) => String(val).toLowerCase() === filter_text,
    );

  // --- KEYBOARD NAVIGATION ---
  const handleContainerKeyDown = (e) => {
    if (!containerRef.current) return;

    if (e.key === "Escape") {
      handleClose();
      return;
    }

    const focusableElements = Array.from(
      containerRef.current.querySelectorAll("input, button"),
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const currentIndex = focusableElements.indexOf(document.activeElement);

    let nextElement = null;

    if (e.key === "Tab") {
      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        nextElement = firstElement;
      } else if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        nextElement = lastElement;
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      nextElement = focusableElements[nextIndex];
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        (currentIndex - 1 + focusableElements.length) %
        focusableElements.length;
      nextElement = focusableElements[prevIndex];
    }

    if (nextElement) {
      nextElement.focus();
      nextElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      if (filtered_base_options.length > 0) {
        const firstOpt = filtered_base_options[0];
        const optVal = typeof firstOpt === "string" ? firstOpt : firstOpt.name;
        handleToggle(e, optVal);
      } else if (isAllowOtherAnswer && raw_filter_text.trim() !== "") {
        handleToggle(e, raw_filter_text.trim());
      }
    } else if (inPopup && onKeyDown && !isDropdownOpen) {
      // Pass other keys up to popup editor if menu is closed
      onKeyDown(e);
    }
  };

  // --- STYLES ---
  const baseItemStyle = {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "bottom",
  };

  const flexContainerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  // --- SHARED MENU CONTENT ---
  const renderMenuContent = () => (
    <div
      ref={containerRef}
      onKeyDown={handleContainerKeyDown}
      style={{
        width: Math.max(250, currentAnchorEl?.offsetWidth || 0),
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
      tabIndex={-1}
    >
      {/* Hide Header in Popup Mode */}
      {!inPopup && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <p className="text_small" style={{ color: "#666", margin: 0 }}>
            Editing: {activeField?.name || activeField?.key}
          </p>
          <button
            className="badge_small cursor_pointer background_grey_light"
            style={{ border: "none", borderRadius: "4px", outline: "none" }}
            onClick={handleClose}
          >
            Done
          </button>
        </div>
      )}

      {/* SEARCH INPUT */}
      <input
        ref={searchRef}
        type="text"
        className={
          inPopup ? "input_table margin_bottom" : "input margin_bottom"
        }
        placeholder="Search..."
        value={raw_filter_text}
        onChange={filterOnChange}
        onKeyDown={handleInputKeyDown}
        style={{
          width: "100%",
          boxSizing: "border-box",
          marginBottom: "8px",
          padding: "8px",
          borderRadius: "4px",
          border: "1px solid #ccc",
          outlineColor: "black",
        }}
      />

      {/* SCROLLABLE OPTIONS LIST */}
      <div
        className="option_list_box_dropdown"
        style={{
          maxHeight: inPopup ? "200px" : "300px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          padding: "2px",
        }}
      >
        {currentValues.length > 0 && (
          <React.Fragment>
            <button
              onClick={handleClearAll}
              className="badge_small background_grey_light cursor_pointer a11y_dropdown_item"
              style={{
                ...baseItemStyle,
                ...flexContainerStyle,
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
            >
              <div
                style={{ width: "14px", marginRight: "8px", flexShrink: 0 }}
              />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  opacity: 0.8,
                }}
              >
                Clear All
              </span>
            </button>
            <hr style={{ margin: "2px 0", borderTop: "1px solid #ccc" }} />
          </React.Fragment>
        )}

        {/* 1. Base Options */}
        {filtered_base_options.map((opt, idx) => {
          const optValue = typeof opt === "string" ? opt : opt.name;
          const isSelected = currentValues.includes(optValue);

          return (
            <button
              key={`base-${idx}`}
              onClick={(e) => handleToggle(e, optValue)}
              title={optValue}
              style={{
                ...baseItemStyle,
                ...flexContainerStyle,
                width: "100%",
                textAlign: "left",
                border: "none",
              }}
              className="badge_small background_grey_light cursor_pointer a11y_dropdown_item"
            >
              {/* <div
                className={isSelected ? "checklist_active" : "checklist_not"}
                style={{ marginRight: "8px", flexShrink: 0 }}
              /> */}
              <Checkbox
                checked={isSelected}
                style={{ margin: 0, padding: 0, marginRight: "8px" }}
              />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                {optValue}
              </span>
            </button>
          );
        })}

        {/* 2. Custom Answer */}
        {isAllowOtherAnswer && raw_filter_text && !hasExactMatch && (
          <React.Fragment>
            {filtered_base_options.length > 0 && (
              <hr style={{ margin: "2px 0", borderTop: "1px solid #ccc" }} />
            )}
            <button
              onClick={(e) => handleToggle(e, raw_filter_text.trim())}
              title={`Use custom answer: ${raw_filter_text.trim()}`}
              style={{
                ...baseItemStyle,
                ...flexContainerStyle,
                width: "100%",
                textAlign: "left",
                border: "none",
              }}
              className="badge_small background_grey_light cursor_pointer a11y_dropdown_item"
            >
              <div
                className="checklist_active"
                style={{ marginRight: "8px", flexShrink: 0 }}
              />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                + "{raw_filter_text.trim()}"
              </span>
            </button>
          </React.Fragment>
        )}

        {/* 3. Missing Selected Options */}
        {missing_selected_values.map((val, idx) => (
          <button
            key={`missing-${idx}`}
            onClick={(e) => handleToggle(e, val)}
            title={val}
            style={{
              ...baseItemStyle,
              ...flexContainerStyle,
              width: "100%",
              textAlign: "left",
              border: "none",
            }}
            className="badge_small background_grey_light cursor_pointer a11y_dropdown_item"
          >
            <div
              className="checklist_active"
              style={{ marginRight: "8px", flexShrink: 0 }}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "left",
              }}
            >
              {val}
            </span>
          </button>
        ))}

        {filtered_base_options.length === 0 &&
          missing_selected_values.length === 0 &&
          !isAllowOtherAnswer && (
            <p
              className="text_small"
              style={{ color: "#999", textAlign: "center", marginTop: "4px" }}
            >
              No options found.
            </p>
          )}
      </div>
    </div>
  );

  if (inPopup) {
    const mode_map = properties_state.mode_map;

    const displayValue = currentValues.join(", ");

    return (
      <main>
        {mode_map === "editor" ? (
          <section>
            <div
              ref={popupAnchorRef}
              className="input margin_bottom"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              tabIndex={0}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "#fff",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                height: "auto", // allow to expand slightly if many items, or set minHeight: 36px
                minHeight: "36px",
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsDropdownOpen(true);
                } else if (onKeyDown) {
                  onKeyDown(e);
                }
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayValue ? (
                  displayValue
                ) : (
                  <span style={{ color: "#999" }}>Select options...</span>
                )}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  flexShrink: 0,
                  marginLeft: "8px",
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>

            {isDropdownOpen && (
              <FLOAT_MENU
                id={`popup-multi-menu-${activeField?.key}`}
                open={true}
                anchorEl={popupAnchorRef.current}
                onClose={handleClose}
              >
                {renderMenuContent()}
              </FLOAT_MENU>
            )}
          </section>
        ) : (
          <div className="container background_white text_medium">
            {displayValue}
          </div>
        )}
      </main>
    );
  }

  // --- TABLE MODE RETURN ---
  return (
    <FLOAT_MENU
      id="global-cell-editor-multi"
      open={true}
      anchorEl={editorState?.anchorEl}
      onClose={handleClose}
    >
      {renderMenuContent()}
    </FLOAT_MENU>
  );
}
